import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import Navbar from "../../components/Navbar";
import MarketingBackdrop from "../../components/MarketingBackdrop";
import SEO, { SITE_URL } from "../../components/SEO";
import { useUpgrade } from "../../components/gating/UpgradeContext";
import { meetsFeature } from "../../config/tiers";
import { AuthContext } from "../../context/AuthContext";
import {
  getMarketplaceAdvisor,
  subscribeToPlan,
  listMySubscriptions,
} from "../../api/marketplaceApi";

// Public profile + plan-purchase surface. Visitors who aren't logged in
// see the same content but the Subscribe button routes them to /login first.

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Banner = ({ kind, children }) => {
  const cls =
    kind === "error"
      ? "bg-red-500/10 text-red-500 border-red-500/40"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/40";
  const Icon = kind === "error" ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const AdvisorAvatar = ({ src, name, size = 96 }) => (
  <div
    style={{ width: size, height: size }}
    className="rounded-full overflow-hidden flex-shrink-0 bg-indigo-600 flex items-center justify-center font-bold text-white"
  >
    {src ? (
      <img src={src} alt={name} className="w-full h-full object-cover" />
    ) : (
      <span style={{ fontSize: size * 0.4 }}>
        {(name || "?")
          .split(" ")
          .map((s) => s[0])
          .filter(Boolean)
          .slice(0, 2)
          .join("")
          .toUpperCase()}
      </span>
    )}
  </div>
);

// Best-effort YouTube/Vimeo embed extraction. For arbitrary mp4 URLs we
// just drop a native <video> player.
const renderVideo = (url) => {
  if (!url) return null;
  const ytMatch = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/,
  );
  if (ytMatch) {
    return (
      <div className="aspect-video w-full rounded-xl overflow-hidden">
        <iframe
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          title="Advisor intro"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return (
      <div className="aspect-video w-full rounded-xl overflow-hidden">
        <iframe
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          title="Advisor intro"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }
  return (
    <video controls className="w-full rounded-xl">
      <source src={url} />
    </video>
  );
};

const formatPrice = (minor, currency, interval) => {
  if (!Number.isFinite(minor)) return `${currency}/${interval}`;
  return `${currency} ${(minor / 100).toFixed(2)} / ${interval}`;
};

const AdvisorPublicProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, profile } = useContext(AuthContext);
  const { promptUpgrade } = useUpgrade();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSubs, setActiveSubs] = useState({});  // {planId: subId}
  const [busyPlan, setBusyPlan] = useState(null);
  const [banner, setBanner] = useState(null);

  const loadAll = async () => {
    try {
      const d = await getMarketplaceAdvisor(slug);
      setData(d);
      if (isAuthenticated) {
        try {
          const subs = await listMySubscriptions();
          const map = {};
          for (const s of subs || []) {
            if (s.status === "active") map[s.plan] = s.id;
          }
          setActiveSubs(map);
        } catch {
          /* not critical */
        }
      }
    } catch {
      setData({ notFound: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isAuthenticated]);

  const flash = (kind, message) => {
    setBanner({ kind, message });
    setTimeout(() => setBanner(null), 4000);
  };

  const onSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate(`/login?next=/marketplace/advisor/${slug}`);
      return;
    }
    // Basic can't subscribe at all; show the upgrade prompt instead of failing.
    if (!meetsFeature(profile?.tier, "advisor_subscribe")) {
      promptUpgrade("advisor_subscribe");
      return;
    }
    setBusyPlan(planId);
    try {
      const sub = await subscribeToPlan(planId);
      setActiveSubs((prev) => ({ ...prev, [planId]: sub.id }));
      flash(
        "success",
        "Subscribed. The agents will start applying this skill on your next session.",
      );
    } catch (err) {
      // Backend may 402 when over the tier's subscription limit → upgrade.
      if (err?.response?.status === 402 && err?.response?.data?.upgrade_required) {
        promptUpgrade(err.response.data.feature || "advisor_subscribe");
        return;
      }
      const detail = err?.response?.data?.detail || "Could not subscribe.";
      flash("error", detail);
    } finally {
      setBusyPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 bg-gray-50 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black">
        <MarketingBackdrop />
        <Navbar />
        <div className="relative max-w-4xl mx-auto px-6">
          <Card>Loading…</Card>
        </div>
      </div>
    );
  }

  if (!data || data.notFound) {
    return (
      <div className="min-h-screen pt-28 bg-gray-50 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black">
        <MarketingBackdrop />
        <Navbar />
        <div className="relative max-w-4xl mx-auto px-6">
          <Card>
            <p className="font-bold">Advisor not found.</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              They may have paused or this slug doesn't exist.
            </p>
            <Link
              to="/marketplace"
              className="inline-block mt-3 text-indigo-500 hover:underline text-sm font-semibold"
            >
              ← Back to marketplace
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const { advisor, plans } = data;

  const advisorDesc = (advisor.bio || advisor.headline || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 155);

  return (
    <div className="min-h-screen pt-28 pb-12 bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white relative">
      <SEO
        path={`/marketplace/advisor/${slug}`}
        title={`${advisor.display_name}${advisor.headline ? ` — ${advisor.headline}` : ""}`}
        description={
          advisorDesc ||
          `${advisor.display_name} on FinAI — view profile, qualifications, and subscription plans.`
        }
        type="profile"
        image={advisor.headshot_url || `${SITE_URL}/og-image.png`}
      />
      <MarketingBackdrop />
      <Navbar />

      <div className="relative max-w-4xl mx-auto px-6 space-y-6">
        <Link
          to="/marketplace"
          className="inline-block text-sm text-gray-500 hover:text-indigo-500"
        >
          ← Back to marketplace
        </Link>

        {banner && <Banner kind={banner.kind}>{banner.message}</Banner>}

        {/* Header card — headshot + name + headline + jurisdiction */}
        <Card>
          <div className="flex items-start gap-5">
            <AdvisorAvatar src={advisor.headshot_url} name={advisor.display_name} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-bold">{advisor.display_name}</h1>
                {advisor.jurisdiction && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-500">
                    {advisor.jurisdiction}
                  </span>
                )}
              </div>
              {advisor.headline && (
                <p className="text-base text-gray-600 dark:text-gray-400 mt-2">
                  {advisor.headline}
                </p>
              )}
            </div>
          </div>
        </Card>

        {advisor.intro_video_url && (
          <Card>
            <h2 className="text-lg font-bold mb-3">Intro</h2>
            {renderVideo(advisor.intro_video_url)}
          </Card>
        )}

        {advisor.bio && (
          <Card>
            <h2 className="text-lg font-bold mb-2">About</h2>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
              {advisor.bio}
            </p>
          </Card>
        )}

        {(advisor.qualifications || []).length > 0 && (
          <Card>
            <h2 className="text-lg font-bold mb-3">Qualifications</h2>
            <ul className="space-y-1.5">
              {advisor.qualifications.map((q, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  <span>{q.label}</span>
                  {q.verified && (
                    <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                      Verified
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {(advisor.achievements || []).length > 0 && (
          <Card>
            <h2 className="text-lg font-bold mb-3">Track record</h2>
            <ul className="space-y-2">
              {advisor.achievements.map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="text-[11px] font-bold text-indigo-500 w-12 shrink-0 mt-0.5">
                    {a.year || "—"}
                  </span>
                  <span>{a.title}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Plans */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Plans</h2>
          {plans.length === 0 ? (
            <Card>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This advisor has no live plans yet.
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {plans.map((p) => {
                const subscribed = !!activeSubs[p.id];
                return (
                  <Card key={p.id}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold">{p.name}</h3>
                        <p className="text-base font-mono mt-1">
                          {formatPrice(p.price_minor_units, p.currency, p.billing_interval)}
                        </p>
                        {p.summary && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            {p.summary}
                          </p>
                        )}
                        {(p.skill_summaries || []).length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {p.skill_summaries.map((s) => (
                              <span
                                key={s.id}
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500"
                              >
                                {s.name} · risk: {s.risk_band}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0">
                        {subscribed ? (
                          <span className="px-4 py-2 rounded-lg bg-emerald-500/15 text-emerald-500 text-sm font-semibold inline-block">
                            Subscribed ✓
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onSubscribe(p.id)}
                            disabled={busyPlan === p.id}
                            className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50"
                          >
                            {busyPlan === p.id ? "Subscribing…" : "Subscribe"}
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 text-center pt-4 max-w-2xl mx-auto">
          Recommendation-only — the AI agents will surface actions following this strategy
          but you approve every trade. Advisors never see your identity or portfolio.
        </p>
      </div>
    </div>
  );
};

export default AdvisorPublicProfile;
